import { ChangeEventHandler, ComponentType, ReactNode } from "react";
import { Control, Controller, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { InputAttributes, NumberFormatValues, NumericFormat, OnValueChange } from "react-number-format";
import { CustomLabel } from "./CustomLabel";
import { InputErrorMessage } from "./InputErrorMessage";

type CustomNumberInputWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
  isPassword?: boolean;
};

type CustomNumberInputWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validacoes*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  isPassword?: boolean;
};

type CustomNumberFormatProps = {
  id: string;
  type?: "text" | "tel" | "password";
  label: string;
  value?: string | number | null;
  suffix?: string;
  prefix?: string;
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined;
  onBlur?: ChangeEventHandler<HTMLInputElement> | undefined;
  required?: boolean;
  className?: string;
  isAllowed?: (values: NumberFormatValues) => boolean; //Função que retorna a permissão para considerar determinado valor/floatValue - formattedValue - value
  placeholder?: string;
  customInput?: ComponentType<InputAttributes>; //Atributo que define o tipo de campo que o número sera renderizado
  decimalScale?: number; //Número de casas decimais que o input irá aceitar
  defaultValue?: string | number | null;
  onValueChange?: OnValueChange;
  allowNegative?: boolean;
  isVisualizacao?: boolean;
  allowLeadingZeros?: boolean; //Atributo que define se o campo irá aceitar zeros a esquerda
  fixedDecimalScale?: boolean; //Número de casas decimais fixas no input
  allowedDecimalSeparators?: string[];
} & (CustomNumberInputWithValidationProps | CustomNumberInputWithoutValidationProps);

const CustomNumberInput: React.FC<CustomNumberFormatProps> = (props) => {
  const getCustomStyle = (possuiErro: boolean) => {
    return possuiErro ? { border: "1px solid #dc3545", transition: "none" } : {};
  };
  const renderCustomNumberInputComValidacao = (): ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && (
                <CustomLabel possuiErro={fieldState.error != null}>
                  {props.required ? `${props.label} *` : props.label}
                </CustomLabel>
              )}
              <div className="d-flex">
                <NumericFormat
                  {...props.register(props.name)}
                  {...field}
                  type={props.type ? props.type : "text"}
                  style={getCustomStyle(fieldState.error !== undefined)}
                  prefix={props.prefix}
                  suffix={props.suffix}
                  className="form-control input-gc"
                  isAllowed={props.isAllowed}
                  placeholder={props.placeholder}
                  customInput={props.customInput}
                  displayType={props.isVisualizacao ? "text" : "input"}
                  defaultValue={props.defaultValue}
                  decimalScale={props.decimalScale}
                  onValueChange={(values) => {
                    field.onChange(values.floatValue);
                  }}
                  // onChange={(event) => {
                  //   field.onChange(event);
                  //   props.setValue(props.name, event.target.value);
                  //   props.onChange && props.onChange(event);
                  // }}
                  allowNegative={props.allowNegative}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={props.fixedDecimalScale}
                  allowLeadingZeros={props.allowLeadingZeros}
                  allowedDecimalSeparators={props.allowedDecimalSeparators}
                  onBlur={(event) => {
                    props.onBlur && props.onBlur(event);
                  }}
                />
              </div>
              {fieldState.error && <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>}
            </>
          )}
        />
      );
    }
  };

  const renderCustomNumberInputSemValidacao = (): ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && <CustomLabel possuiErro={false}>{props.label}</CustomLabel>}
          <NumericFormat
            type={props.type ? props.type : "text"}
            value={props.value}
            style={{ color: "#707070" }}
            prefix={props.prefix}
            suffix={props.suffix}
            className="form-control input-gc"
            isAllowed={props.isAllowed}
            customInput={props.customInput}
            placeholder={props.placeholder}
            displayType={props.isVisualizacao ? "text" : "input"}
            defaultValue={props.defaultValue}
            decimalScale={props.decimalScale}
            onValueChange={(event, sourceInfo) => {
              props.onValueChange && props.onValueChange(event, sourceInfo);
            }}
            allowNegative={props.allowNegative}
            decimalSeparator=","
            thousandSeparator="."
            fixedDecimalScale={props.fixedDecimalScale}
            allowLeadingZeros={props.allowLeadingZeros}
            allowedDecimalSeparators={props.allowedDecimalSeparators}
          />
        </>
      );
    }
  };

  return (
    <div className={props.className || ""}>
      {props.usarValidacaoParaFormulario && renderCustomNumberInputComValidacao()}
      {!props.usarValidacaoParaFormulario && renderCustomNumberInputSemValidacao()}
    </div>
  );
};

export default CustomNumberInput;
